import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, styled, Tooltip } from "@mui/material";
import clsx from "clsx";
import { CSSCollapseTransitionComponent } from "../../patientCard/layout/CSSCollapseTransitionComponent";

//region [[ Styles ]]

const ConfigurationTabsView = styled("div")({
  display: "flex",
  flexFlow: "column",
  width: "100%",
  height: "100%",
  transition: "all 0.3s ease-out",
  position: "relative",
});

const TopBarArea = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  borderBottom: "1px solid #E6E4FF",
  //marginBottom: "29px",
  flexWrap: "wrap-reverse",
  width: "100%",
  // overflowX: "auto",
  transition: "all 0.3s ease-out",
});

const TabsButtonContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "stretch",
  position: "relative",
});

const ChildrenContainer = styled("div")({
  display: "flex",
  flexFlow: "row-reverse",
  margin: "15px",
  flex: 1,
});

const TabButton = styled(Button)({
  padding: "20px 25px",
  fontWeight: 500,
  color: "#243443",
  transition: "all 0.2s ease-out",
  letterSpacing: "0.39998px",
  cursor: "pointer",
  "&.tab-selected": {
    color: "#4247C9",
    letterSpacing: "0.39998px",
    textShadow: "0px 0px 1px #4247C9",
  },
  textTransform: "none",
  fontSize: "12px",
  lineHeight: 1.2,
  borderTopLeftRadius: "8px",
  borderTopRightRadius: "8px",
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
});
const SelectedContainer = styled("div")({
  position: "absolute",
  borderTopLeftRadius: "8px",
  borderTopRightRadius: "8px",
  height: "3px",
  background: "#4247C9",
  bottom: 0,
  left: 0,
  transition: "all 0.3s ease-out",
  width: "100%",
});
const TabsContent = styled("div")({
  overflow: "auto",
  width: "100%",
  padding: "10px",
});
//endregion [[ Styles ]]

//region [[ Props ]]
export type ConfigurationTab = {
  key: string;
  name: string;
  tooltip: string;
  component: React.ReactNode;
};

export interface ConfigurationTabsProps {
  selectedIndex: number;
  className?: string;
  tabs?: ConfigurationTab[];
  onTabSelected?: (
    tab: ConfigurationTab,
    index: number,
    section?: string,
  ) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  inExtension?: boolean;
  stickyTabs?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

const TooltipIfEnabled = ({
  children,
  key,
  title,
  inExtension = true,
  ...props
}: {
  children: any;
  key: string;
  title: string;
  inExtension?: boolean;
}) => {
  if (inExtension) {
    return <React.Fragment key={key}>{children}</React.Fragment>;
  }

  return (
    <Tooltip
      TransitionComponent={
        inExtension ? CSSCollapseTransitionComponent : undefined
      }
      key={key}
      title={title}
    >
      {children}
    </Tooltip>
  );
};

export const KahunSliderTabs = ({
  selectedIndex,
  inExtension = false,
  ...props
}: ConfigurationTabsProps) => {
  const [currentSelected, setCurrentSelectedIndex] = useState(selectedIndex);

  const tabs = useMemo(() => props.tabs?.filter((t) => t), [props.tabs]);

  const selectedRef = useRef<HTMLButtonElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const [currentCursorPos, setCurrentCursorPos] = useState({ x: 0, width: 0 });

  const handleTabClicked = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number,
  ) => {
    props.onTabSelected?.(tabs[index], index);
    setCurrentSelectedIndex(index);
  };

  useEffect(() => {
    setCurrentSelectedIndex(selectedIndex);
  }, [selectedIndex]);

  const takeMarkToSelectedTab = () => {
    if (selectedRef.current) {
      const containerPos = containerRef?.current?.getBoundingClientRect?.() || {
        left: 0,
        width: 0,
      };

      const pos = selectedRef.current?.getBoundingClientRect?.() || {
        left: 0,
        width: 0,
      };
      setCurrentCursorPos({
        x: pos.left - containerPos.left,
        width: pos.width,
      });
      selectedRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    takeMarkToSelectedTab();
    // Add event listener
    window.addEventListener("resize", takeMarkToSelectedTab);
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", takeMarkToSelectedTab);
  }, [currentSelected]);

  return (
    <ConfigurationTabsView
      className={clsx("kahun-slider-tabs", props.className)}
    >
      <TopBarArea className={"top-bar-area"}>
        <TabsButtonContainer ref={containerRef}>
          {tabs.map((t, i) => (
            <TooltipIfEnabled
              inExtension={inExtension}
              key={t.key}
              title={t.tooltip}
            >
              <>
                <TabButton
                  className={i === currentSelected ? "tab-selected" : ""}
                  ref={i === currentSelected ? selectedRef : null}
                  onClick={(e) => handleTabClicked(e, i)}
                  disabled={props.disabled}
                >
                  {t.name}
                </TabButton>
              </>
            </TooltipIfEnabled>
          ))}
          <SelectedContainer
            style={{ left: currentCursorPos.x, width: currentCursorPos.width }}
          />
        </TabsButtonContainer>
        {!!props.children && (
          <ChildrenContainer>{props.children}</ChildrenContainer>
        )}
      </TopBarArea>
      <TabsContent className={"tabs-content"}>
        {tabs?.[currentSelected > 0 ? currentSelected : 0]?.component ||
          "Error - index=" + currentSelected + " has no component set to it"}
      </TabsContent>
    </ConfigurationTabsView>
  );
};
